import {
	readDeviceStatus,
	readDeviceSettings,
} from "src/stores/device-runtime/status-selectors";
import type { Device } from "src/types/Device";
import type {
	DeviceUIDeviceSidePanelView,
	ViewCategory,
	ViewVariant,
} from "src/types/DeviceUI";
import { devices_config } from "src/configs/devices";
import { get_mode, isPill, isFanDevice } from "src/helpers/interface_helpers";
import { pickViewVariant } from "src/helpers/sidepanel_view_variant";
import { deviceViewOptionsRegistry, type ViewOption } from "./view-registry";
import {
	getSidepanelViewSelection,
	isSidepanelViewSwitcherAvailable,
} from "src/stores/features/dev/sidepanel-view-switcher";

export { pickViewVariant } from "src/helpers/sidepanel_view_variant";

const DEFAULT_VARIANTS: ViewVariant[] = ["basic", "advanced"];

export function getDeviceCategory(device: Device): ViewCategory {
	if (isFanDevice(device?.type)) return "fan";

	if (device?.type === "SHSW-25") {
		const status = readDeviceStatus(device?.id);
		const settings = readDeviceSettings(device?.id);
		if (get_mode(device, status, settings) === "roller") return "roller";
	}
	if (device?.category === "light") return "light";
	if (device?.category === "relay") return "relay";
	if (device?.category === "roller") return "roller";
	if (isPill(device?.type)) return "pill_sensor";
	if (device?.gen === "GBLE" && device?.category === "sensor")
		return "bluetooth_sensor";
	if (device?.category === "sensor") return "sensor";
	if (device?.category === "power_meter") return "power_meter";
	if (device?.category === "emeter") return "emeter";
	if (device?.category === "inputs_reader") return "inputs_reader";
	if (device?.category === "remote_control") return "remote_control";
	if (device?.type === "peripheral-device") return "peripheral_device";

	return "relay";
}

function getDeviceMode(device: Device): string | undefined {
	const status = readDeviceStatus(device?.id);
	const settings = readDeviceSettings(device?.id);
	return get_mode(device, status, settings);
}

function registryVariantsForCategory(category: ViewCategory): ViewVariant[] {
	const options = deviceViewOptionsRegistry[category];
	if (options?.length) return options.map((option) => option.variant);
	return DEFAULT_VARIANTS;
}

function getConfiguredViewVariants(device: Device): ViewVariant[] {
	const config = devices_config?.[device?.type];
	if (
		Array.isArray(config?.availableViews) &&
		config.availableViews.length > 0
	) {
		return config.availableViews as ViewVariant[];
	}

	const modes = config?.modes;
	if (modes && !Array.isArray(modes)) {
		const mode = getDeviceMode(device);
		const fromMode = mode ? modes[mode]?.availableViews : undefined;
		if (Array.isArray(fromMode) && fromMode.length > 0) {
			return fromMode as ViewVariant[];
		}

		const category = getDeviceCategory(device);
		const fromCategory = modes[category]?.availableViews;
		if (Array.isArray(fromCategory) && fromCategory.length > 0) {
			return fromCategory as ViewVariant[];
		}
	}

	return registryVariantsForCategory(getDeviceCategory(device));
}

export function getAvailableViews(device: Device): ViewOption[] {
	const category = getDeviceCategory(device);
	const allowed = getConfiguredViewVariants(device);
	const registryOptions = deviceViewOptionsRegistry[category] ?? [];

	return allowed.map((variant) => {
		const fromRegistry = registryOptions.find(
			(option) => option.variant === variant,
		);
		return (
			fromRegistry ?? {
				category,
				variant,
				title: variant.charAt(0).toUpperCase() + variant.slice(1),
			}
		);
	});
}

export function getCurrentView(
	device: Device,
	options?: { switcherEnabled?: boolean; selected?: ViewVariant },
): DeviceUIDeviceSidePanelView {
	const category = getDeviceCategory(device);
	const allowed = getConfiguredViewVariants(device);
	const switcherEnabled =
		options?.switcherEnabled ?? isSidepanelViewSwitcherAvailable();
	const selected =
		options?.selected ??
		(switcherEnabled ? getSidepanelViewSelection(device?.id) : undefined);

	return {
		category,
		variant: pickViewVariant(allowed, { switcherEnabled, selected }),
	};
}
